import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';

interface FormattedHelpTextProps {
  text: string;
}

interface CollapsibleExampleProps {
  content: string[];
  maxVisibleLines?: number;
  title: string;
}

interface Section {
  type: string;
  content: string[];
}

const CollapsibleExample: React.FC<CollapsibleExampleProps> = ({ 
  content, 
  maxVisibleLines = 0,
  title 
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const lines = content.filter(line => line.trim() !== '');
  const shouldCollapse = lines.length > maxVisibleLines;

  // Only show first maxVisibleLines lines initially
  const displayedLines = shouldCollapse ? lines.slice(0, maxVisibleLines) : lines;
  
  return (
    <>
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 font-mono text-sm">
          {displayedLines.map((line, i) => (
            <div key={i} className="text-green-700 whitespace-pre-wrap">
              {line}
            </div>
          ))}
          {shouldCollapse && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Show more...
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 font-mono text-sm">
              {lines.map((line, i) => (
                <div key={i} className="text-green-700 whitespace-pre-wrap">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Utility functions for section type checking
const hasMarkers = (content: string[]): boolean => 
  content.some(line => line.startsWith('✓') || line.startsWith('✗') || line.startsWith('•'));

const isPlainExample = (section: Section): boolean => 
  (section.type === 'Example:' || section.type === 'Examples:') && !hasMarkers(section.content);

const isGoodExample = (section: Section): boolean => 
  (section.type === 'Examples:' || section.type === 'Additional examples:') && 
  section.content.some(line => line.startsWith('✓'));

// Section component renderers
const PlainExampleSection: React.FC<{ section: Section; index: number }> = ({ section, index }) => (
  <div key={index} className="space-y-4">
    <h3 className="font-medium text-gray-900">{section.type}</h3>
    <CollapsibleExample 
      content={section.content} 
      title={`${section.type.slice(0, -1)} Details`}
      maxVisibleLines={1}
    />
  </div>
);

const MarkedListSection: React.FC<{ section: Section; index: number; markerColor: string; markerChar: string }> = ({ 
  section, index, markerColor, markerChar 
}) => (
  <div key={index} className="space-y-2">
    <h3 className="font-medium text-gray-900">{section.type}</h3>
    {section.content.map((line, i) => {
      if (line.startsWith(markerChar)) {
        return (
          <div key={i} className="flex items-start space-x-2 pl-4">
            <span className={`${markerColor} font-bold`}>{markerChar}</span>
            <span className="text-gray-700">{line.substring(2)}</span>
          </div>
        );
      }
      return null;
    })}
  </div>
);

const BulletListSection: React.FC<{ section: Section; index: number }> = ({ section, index }) => (
  <div key={index} className="space-y-1">
    <h3 className="font-medium text-gray-900">{section.type}</h3>
    {section.content.map((line, i) => {
      if (line.startsWith('•')) {
        return (
          <div key={i} className="flex items-start space-x-2 pl-4">
            <span className="text-gray-400">•</span>
            <span className="text-gray-700">{line.substring(2)}</span>
          </div>
        );
      }
      return null;
    })}
  </div>
);

const RegularSection: React.FC<{ section: Section; index: number }> = ({ section, index }) => (
  <p key={index} className="text-gray-700">
    {section.content[0]}
  </p>
);

// Main section renderer
const renderSection = (section: Section, index: number): React.ReactNode => {
  // Plain text examples
  if (isPlainExample(section)) {
    return <PlainExampleSection section={section} index={index} />;
  }

  // Notes section
  if (section.type === 'Notes:') {
    return <BulletListSection section={section} index={index} />;
  }

  // Good examples
  if (isGoodExample(section)) {
    return <MarkedListSection section={section} index={index} markerColor="text-green-600" markerChar="✓" />;
  }

  // Bad examples
  if (section.type === 'Avoid:') {
    return <MarkedListSection section={section} index={index} markerColor="text-red-600" markerChar="✗" />;
  }

  // Best practices
  if (section.type === 'Best practices:') {
    return <BulletListSection section={section} index={index} />;
  }

  // Regular content
  if (section.type === 'regular') {
    return <RegularSection section={section} index={index} />;
  }

  return null;
};

// Text parsing utility
const parseTextIntoSections = (text: string): Section[] => {
  const lines = text.split('\n');
  const sections: Section[] = [];
  let currentSection = '';
  let currentContent: string[] = [];

  const addCurrentSection = () => {
    if (currentContent.length > 0) {
      sections.push({
        type: currentSection,
        content: [...currentContent]
      });
      currentContent = [];
    }
  };

  const sectionHeaders = ['Example:', 'Examples:', 'Best practices:', 'Additional examples:', 'Avoid:', 'Notes:'];

  lines.forEach(line => {
    if (sectionHeaders.includes(line)) {
      addCurrentSection();
      currentSection = line;
    } else if (line.trim() === '') {
      if (currentContent.length > 0) {
        addCurrentSection();
      }
    } else if (line.trim()) {
      if (!currentSection) {
        sections.push({ type: 'regular', content: [line] });
      } else {
        currentContent.push(line);
      }
    }
  });

  addCurrentSection();
  return sections;
};

const FormattedHelpText: React.FC<FormattedHelpTextProps> = ({ text }) => {
  const sections = parseTextIntoSections(text);

  return (
    <div className="space-y-4">
      {sections.map((section, index) => renderSection(section, index))}
    </div>
  );
};

export default FormattedHelpText;